import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Switch,
  Menu,
  FormControlLabel,
  Card,
  CardContent,
} from "@mui/material";
import {
  Tune as TuneIcon,
  AutoAwesome as AutoAwesomeIcon,
  YouTube as YouTubeIcon,
  AttachFile as AttachFileIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Edit as EditIcon,
  LinkedIn as LinkedInIcon,
  ContentCopy as ContentCopyIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { useCredits } from "../../hooks/useCredits";
import LinkedInPreview from "../../components/preview/LinkedInPreview";
import { useSnackbar } from "notistack";
import { handleImageUpload } from "./imageAnalysisService";
import {
  handleArticleSubmit,
  handleGenerateFromArticle,
} from "./articleService";
import { handleGenerate, handleChange } from "./customGenerationService";
import TypeWriter from "../../components/common/TypeWriter";
import ContentScorer from "../../components/content-scoring/ContentScorer";

const GeneratePostNew = () => {
  const { user } = useAuth();
  const { refetchCredits } = useCredits();
  const { enqueueSnackbar } = useSnackbar();

  // Form data state matching the working version
  const [formData, setFormData] = useState({
    topic: "",
    writingStyle: null,
    goals: [],
    industry: "",
    professionalRole: "",
    targetAudience: "",
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  });

  // Main states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedContent, setGeneratedContent] = useState("");
  const [generatedHashtags, setGeneratedHashtags] = useState([]);
  const [activeTab, setActiveTab] = useState("custom");

  // Settings states
  const [modificationAnchorEl, setModificationAnchorEl] = useState(null);
  const [useAdvancedSettings, setUseAdvancedSettings] = useState(false);
  const [contentPreferencesEnabled, setContentPreferencesEnabled] =
    useState(false);
  const [toneAnalysisEnabled, setToneAnalysisEnabled] = useState(true);

  // Copy and save states
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  // Edit and sharing states
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [isSharing, setIsSharing] = useState(false);

  // Hook selection states
  const [generatedHooks, setGeneratedHooks] = useState([]);
  const [selectedHook, setSelectedHook] = useState(null);
  const [hookLoading, setHookLoading] = useState(false);

  // Tab-specific states
  const [articleUrl, setArticleUrl] = useState("");
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleError, setArticleError] = useState("");
  const [articleContent, setArticleContent] = useState(null);
  const [showArticlePreview, setShowArticlePreview] = useState(false);
  const [imageGeneratedTopic, setImageGeneratedTopic] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState("");
  const [youtubeData, setYoutubeData] = useState(null);
  const [youtubeSubtitles, setYoutubeSubtitles] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [editedTopic, setEditedTopic] = useState("");

  // Model and content preferences
  const [selectedModel, setSelectedModel] = useState("in-model-a");
  const [includeEmojis, setIncludeEmojis] = useState(false);
  const [includeHashtags, setIncludeHashtags] = useState(false);
  const [contentLength, setContentLength] = useState("default");

  // Tone analysis states
  const [userLinkedInPosts, setUserLinkedInPosts] = useState([]);
  const [toneAnalysisLoading, setToneAnalysisLoading] = useState(false);
  const [analyzedTone, setAnalyzedTone] = useState("");

  // Debug generatedContent changes
  React.useEffect(() => {
    console.log("Generated content changed:", generatedContent);
  }, [generatedContent]);

  // Handle form changes exactly like the working version
  const handleFormChange = (e) => {
    handleChange(e, formData, setFormData);
  };

  // Common button styles
  const modernButtonStyle = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: { xs: "0.75rem", sm: "0.9rem" },
    px: { xs: 1, sm: 2 },
    height: { xs: "36px", sm: "40px" },
    borderColor: "var(--primary-color)",
    color: "var(--primary-color)",
    "&:hover": {
      borderColor: "var(--primary-dark)",
      backgroundColor: "var(--primary-light)",
    },
  };

  const modernActionButtonStyle = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
    px: 3,
    height: "40px",
    backgroundColor: "var(--primary-color)",
    color: "white",
    "&:hover": {
      backgroundColor: "var(--primary-dark)",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  };

  // Handle main submission using the working service
  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleGenerateWrapper();
  };

  // Generate hooks using Gemini 2.5 Flash Lite
  const generateHooks = async (topic) => {
    setHookLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ai/generate-hooks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            topic,
            model: "gemini-2.5-flash-lite",
            count: 6,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate hooks");
      }

      const data = await response.json();
      return data.hooks || [];
    } catch (error) {
      console.error("Error generating hooks:", error);
      toast.error("Failed to generate hooks");
      setError("Failed to generate hooks");
      return [];
    } finally {
      setHookLoading(false);
    }
  };

  const handleGenerateWrapper = async () => {
    console.log("=== STARTING HOOK GENERATION ===");

    if (!formData.topic && activeTab === "custom") {
      toast.error("Please enter a topic for the post");
      return;
    }

    let topicToUse = formData.topic;

    // Determine topic based on active tab
    if (activeTab === "image") {
      topicToUse = imageGeneratedTopic;
    } else if (activeTab === "youtube") {
      if (youtubeSubtitles) {
        topicToUse = youtubeSubtitles;
      } else {
        toast.error("No YouTube content available");
        return;
      }
    } else if (activeTab === "article") {
      if (articleContent) {
        topicToUse = articleContent;
      } else {
        toast.error("No article content available");
        return;
      }
    }

    if (!topicToUse) {
      toast.error("A topic is required for generation.");
      return;
    }

    // Generate hooks first
    setHookLoading(true);
    const hooks = await generateHooks(topicToUse);
    setHookLoading(false);
    if (hooks.length > 0) {
      setGeneratedHooks(hooks);
      // Scroll up to show hooks
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Generate the actual post using selected hook
  const generatePostWithHook = async (selectedHook) => {
    console.log("=== STARTING POST GENERATION WITH HOOK ===");

    // Create the parameters object with the selected hook as topic
    const params = {
      formData: { ...formData, topic: selectedHook },
      activeTab: "custom", // Force custom tab since we're using the hook as topic
      imageGeneratedTopic: "",
      youtubeSubtitles: null,
      articleContent: null,
      templateTopic: "",
      selectedTemplate: null,
      toneAnalysisEnabled: false,
      userLinkedInPosts,
      analyzedTone,
      useAdvancedSettings: false,
      selectedModel: "in-model-a", // Use Model A which handles hooks internally
      includeEmojis,
      includeHashtags,
      contentLength,
      contentPreferencesEnabled: false,
      setError,
      setLoading,
      setGeneratedContent,
      setGeneratedHashtags,
      setAnalyzedTone,
    };

    console.log("Generate params with hook:", params);

    try {
      console.log("Calling handleGenerate with selected hook...");
      const result = await handleGenerate(params);
      console.log("handleGenerate returned:", result);

      // Clear the hooks after one is selected
      setGeneratedHooks([]);
      setSelectedHook(selectedHook);
    } catch (error) {
      console.error("Error in handleGenerate:", error);
    }
  };

  // Handle copy functionality
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopySuccess(true);
      enqueueSnackbar("Content copied to clipboard!", { variant: "success" });
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      enqueueSnackbar("Failed to copy content", { variant: "error" });
    }
  };

  // Handle save functionality
  const handleSave = async () => {
    if (!generatedContent) return;

    setSaveLoading(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const postData = {
        content: generatedContent,
        topic: formData.topic || "Custom Post",
        type: "standard",
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/posts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(postData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save post");
      }

      setSaveSuccess("Post saved successfully!");
      enqueueSnackbar("Draft saved successfully!", { variant: "success" });
    } catch (error) {
      const errorMsg = error.message || "Failed to save post";
      setSaveError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: "error" });
    } finally {
      setSaveLoading(false);
    }
  };

  // YouTube functionality matching working version
  const extractVideoId = (url) => {
    const regex =
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const handleYouTubeSubmit = async (e) => {
    e.preventDefault();
    if (!youtubeUrl.trim()) return;

    setYoutubeLoading(true);
    setYoutubeError("");

    try {
      const videoId = extractVideoId(youtubeUrl);
      if (!videoId) {
        throw new Error("Invalid YouTube URL");
      }

      // First get video info
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ai/youtube-video-info`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ url: youtubeUrl }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to analyze YouTube video");
      }

      const data = await response.json();
      setYoutubeData(data);

      // Try to get subtitles
      try {
        const subtitlesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/ai/youtube-subtitles`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({ url: youtubeUrl }),
          }
        );

        if (subtitlesResponse.ok) {
          const subtitlesData = await subtitlesResponse.json();
          setYoutubeSubtitles(subtitlesData);
        }
      } catch (subtitlesError) {
        console.log("Subtitles not available, using video info only");
      }
    } catch (error) {
      setYoutubeError(error.message || "Failed to analyze YouTube video");
    } finally {
      setYoutubeLoading(false);
    }
  };

  const handleGenerateFromYouTube = async () => {
    setFormData((prev) => ({
      ...prev,
      topic: youtubeData?.title || "YouTube video analysis",
    }));
    setActiveTab("youtube");
    await handleGenerateWrapper();
  };

  // Image Upload functionality matching working version
  const handleImageUploadLocal = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await handleImageUpload(
        file,
        setTopicLoading,
        setImageGeneratedTopic,
        setEditedTopic,
        enqueueSnackbar
      );
    } catch (error) {
      setError("Failed to analyze image");
    }
  };

  const handleGenerateFromImage = async () => {
    if (!imageGeneratedTopic) return;
    setActiveTab("image");
    await handleGenerateWrapper();
  };

  // Handle Article functionality
  const handleArticleSubmitLocal = async () => {
    if (!articleUrl.trim()) return;

    try {
      // Create a mock event object
      const mockEvent = { preventDefault: () => {} };

      await handleArticleSubmit(
        mockEvent,
        articleUrl,
        setArticleError,
        setArticleLoading,
        setLoading,
        setArticleContent,
        setShowArticlePreview,
        enqueueSnackbar
      );
    } catch (error) {
      setArticleError("Failed to analyze article");
    }
  };

  const handleGenerateFromArticleLocal = async () => {
    if (!articleContent) return;

    try {
      await handleGenerateFromArticle(
        articleContent,
        setLoading,
        setGeneratedContent,
        setError
      );
    } catch (error) {
      setError("Failed to generate from article");
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header outside the white container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: "2rem",
              fontWeight: 500,
              color: "#1a1a1a",
              textAlign: "center",
              fontFamily: "var(--font-family)",
            }}
          >
            🌟 Ready to create your LinkedIn post?
          </Typography>
        </Box>

        {/* Content Type Buttons - Centered */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mb: 4,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setActiveTab("custom")}
            variant={activeTab === "custom" ? "contained" : "outlined"}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              py: 1,
              px: 2,
              color: activeTab === "custom" ? "white" : "text.primary",
              backgroundColor:
                activeTab === "custom" ? "var(--primary-color)" : "transparent",
              border: "1px solid var(--border-color)",
              "&:hover": {
                backgroundColor:
                  activeTab === "custom"
                    ? "var(--primary-dark)"
                    : "var(--primary-light)",
              },
            }}
          >
            Topic
          </Button>
          <Button
            startIcon={<YouTubeIcon />}
            onClick={() => setActiveTab("youtube")}
            variant={activeTab === "youtube" ? "contained" : "outlined"}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              py: 1,
              px: 2,
              color: activeTab === "youtube" ? "white" : "text.primary",
              backgroundColor:
                activeTab === "youtube"
                  ? "var(--primary-color)"
                  : "transparent",
              border: "1px solid var(--border-color)",
              "&:hover": {
                backgroundColor:
                  activeTab === "youtube"
                    ? "var(--primary-dark)"
                    : "var(--primary-light)",
              },
            }}
          >
            YouTube
          </Button>
          <Button
            startIcon={<AttachFileIcon />}
            onClick={() => setActiveTab("article")}
            variant={activeTab === "article" ? "contained" : "outlined"}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              py: 1,
              px: 2,
              color: activeTab === "article" ? "white" : "text.primary",
              backgroundColor:
                activeTab === "article"
                  ? "var(--primary-color)"
                  : "transparent",
              border: "1px solid var(--border-color)",
              "&:hover": {
                backgroundColor:
                  activeTab === "article"
                    ? "var(--primary-dark)"
                    : "var(--primary-light)",
              },
            }}
          >
            Article
          </Button>
          <Button
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => setActiveTab("image")}
            variant={activeTab === "image" ? "contained" : "outlined"}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              py: 1,
              px: 2,
              color: activeTab === "image" ? "white" : "text.primary",
              backgroundColor:
                activeTab === "image" ? "var(--primary-color)" : "transparent",
              border: "1px solid var(--border-color)",
              "&:hover": {
                backgroundColor:
                  activeTab === "image"
                    ? "var(--primary-dark)"
                    : "var(--primary-light)",
              },
            }}
          >
            Image
          </Button>
        </Box>

        {/* Hooks Section - Displayed when hooks are available */}
        {generatedHooks.length > 0 && (
          <Box sx={{ mb: 3, transition: 'all 0.5s ease-in-out' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Choose Your Hook
            </Typography>
            <Grid container spacing={2}>
              {generatedHooks.map((hook, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      borderRadius: "12px",
                      border: "1px solid var(--border-color)",
                      transition: "all 0.2s ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      "&:hover": {
                        borderColor: "var(--primary-color)",
                        boxShadow: "0 2px 8px rgba(75, 146, 254, 0.1)",
                      },
                    }}
                    onClick={() => generatePostWithHook(hook)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.5,
                        }}
                      >
                        {hook}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Main Input Section - Moved to bottom */}
        <Grid container spacing={3}>
          {/* Left Panel - Input Section */}
          <Grid
            item
            xs={12}
            md={generatedContent ? 6 : 12}
            lg={generatedContent ? 6 : 12}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                border: "1px solid var(--border-color)",
                borderRadius: "12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                },
              }}
            >
              {/* Claude-style Input Area - Only shown for custom tab */}
              {activeTab === "custom" && (
                <Box
                  sx={{
                    mb: 0,
                    flexShrink: 0,
                    flexGrow: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="How can I help you today?"
                    name="topic"
                    value={formData.topic}
                    onChange={handleFormChange}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "rgba(241, 243, 244, 0.5)",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(241, 243, 244, 1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(241, 243, 244, 1)",
                          boxShadow: `0 0 0 2px var(--primary-color)`,
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "1rem",
                        lineHeight: 1.6,
                      },
                    }}
                  />

                  {/* Controls Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    {/* Model Selector and Settings */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      {/* Model Selector */}
                      <FormControl size="small">
                        <Select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          sx={{
                            borderRadius: "8px",
                            backgroundColor: "transparent",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid #e0e0e0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#bdbdbd",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                          }}
                        >
                          <MenuItem value="in-model-a">Model A</MenuItem>
                          <MenuItem value="in-model-b">Model B</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Settings Button */}
                      <IconButton
                        onClick={(e) => {
                          if (modificationAnchorEl) {
                            setModificationAnchorEl(null);
                          } else {
                            setModificationAnchorEl(e.currentTarget);
                          }
                        }}
                        sx={{
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          width: 40,
                          height: 40,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <TuneIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>

                    {/* Generate Button */}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || !formData.topic.trim()}
                      onClick={handleSubmit}
                      sx={modernActionButtonStyle}
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Generate"
                      )}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Error Display */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Claude-style Settings Menu */}
              <Menu
                anchorEl={modificationAnchorEl}
                open={Boolean(modificationAnchorEl)}
                onClose={() => setModificationAnchorEl(null)}
                PaperProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e9ecef",
                    mt: 1,
                    minWidth: 280,
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  {/* Use Style */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 2,
                      borderBottom: "1px solid #f1f3f4",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EditIcon sx={{ fontSize: 18, color: "#6c757d" }} />
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                        Use style
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem", color: "#6c757d" }}>
                        {formData.vibe || "default"}
                      </Typography>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: "#6c757d",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Advanced Settings */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TuneIcon sx={{ fontSize: 18, color: "#6c757d" }} />
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                        >
                          Advanced settings
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#6c757d" }}
                        >
                          Industry, role, audience targeting
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={useAdvancedSettings}
                      onChange={(e) => setUseAdvancedSettings(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4890fe",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4890fe",
                          },
                      }}
                    />
                  </Box>

                  {/* Content Preferences */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AutoAwesomeIcon
                        sx={{ fontSize: 18, color: "#6c757d" }}
                      />
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                        >
                          Content preferences
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#6c757d" }}
                        >
                          Length, emojis, hashtags
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={contentPreferencesEnabled}
                      onChange={(e) =>
                        setContentPreferencesEnabled(e.target.checked)
                      }
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4890fe",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4890fe",
                          },
                      }}
                    />
                  </Box>

                  {/* Tone Analysis */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinkedInIcon sx={{ fontSize: 18, color: "#6c757d" }} />
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                        >
                          Tone analysis
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#6c757d" }}
                        >
                          Analyze your LinkedIn style
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={toneAnalysisEnabled}
                      onChange={(e) => setToneAnalysisEnabled(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4890fe",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4890fe",
                          },
                      }}
                    />
                  </Box>

                  {/* Advanced Settings Form */}
                  {useAdvancedSettings && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f1f3f4" }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleFormChange}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleFormChange}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Target Audience"
                        name="targetAudience"
                        value={formData.targetAudience}
                        onChange={handleFormChange}
                      />
                    </Box>
                  )}

                  {/* Content Preferences Form */}
                  {contentPreferencesEnabled && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f1f3f4" }}>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <Select
                          value={contentLength}
                          onChange={(e) => setContentLength(e.target.value)}
                        >
                          <MenuItem value="short">Short</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="long">Long</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeEmojis}
                            onChange={(e) => setIncludeEmojis(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Include emojis"
                        sx={{ display: "block", mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeHashtags}
                            onChange={(e) =>
                              setIncludeHashtags(e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label="Include hashtags"
                        sx={{ display: "block" }}
                      />
                    </Box>
                  )}
                </Box>
              </Menu>

              {/* Tab-specific Content Area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pt: 2,
                  pr: 2,
                  display: activeTab !== "custom" ? "block" : "none",
                }}
              >
                {/* Custom Tab */}
                {activeTab === "custom" && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: "#6c757d", fontSize: "0.9rem" }}>
                      Enter your topic or idea above to generate a LinkedIn post
                    </Typography>
                  </Box>
                )}

                {/* YouTube Tab */}
                {activeTab === "youtube" && (
                  <Box>
                    <TextField
                      fullWidth
                      label="YouTube URL"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleYouTubeSubmit}
                      disabled={youtubeLoading || !youtubeUrl.trim()}
                      sx={{ ...modernActionButtonStyle, mb: 2 }}
                    >
                      {youtubeLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Analyze Video"
                      )}
                    </Button>

                    {youtubeError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {youtubeError}
                      </Alert>
                    )}

                    {youtubeData && (
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {youtubeData.title}
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleGenerateFromYouTube(youtubeData)
                            }
                            disabled={loading}
                            sx={modernButtonStyle}
                          >
                            Generate LinkedIn Post
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                )}

                {/* Article Tab */}
                {activeTab === "article" && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Article URL"
                      value={articleUrl}
                      onChange={(e) => setArticleUrl(e.target.value)}
                      placeholder="https://example.com/article"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleArticleSubmitLocal}
                      disabled={articleLoading || !articleUrl.trim()}
                      sx={{ ...modernActionButtonStyle, mb: 2 }}
                    >
                      {articleLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Analyze Article"
                      )}
                    </Button>

                    {articleError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {articleError}
                      </Alert>
                    )}

                    {showArticlePreview && articleContent && (
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {articleContent.title}
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={handleGenerateFromArticleLocal}
                            disabled={loading}
                            sx={modernButtonStyle}
                          >
                            Generate LinkedIn Post
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                )}

                {/* Image Tab */}
                {activeTab === "image" && (
                  <Box>
                    <Box
                      sx={{
                        border: "2px dashed #CBD5E0",
                        borderRadius: "8px",
                        p: 4,
                        textAlign: "center",
                        mb: 3,
                        backgroundColor: "#F7FAFC",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUploadLocal}
                        style={{ display: "none" }}
                        id="image-upload"
                      />
                      <label htmlFor="image-upload">
                        <IconButton component="span" sx={{ mb: 2 }}>
                          <AddPhotoAlternateIcon
                            sx={{ fontSize: 48, color: "#4890fe" }}
                          />
                        </IconButton>
                        <Typography
                          variant="body1"
                          sx={{ color: "#4A5568", mb: 1 }}
                        >
                          Click to upload an image
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280" }}>
                          AI will analyze your image and suggest content
                        </Typography>
                      </label>
                    </Box>

                    {topicLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <CircularProgress size={20} />
                        <Typography sx={{ ml: 1 }}>
                          Analyzing image...
                        </Typography>
                      </Box>
                    )}

                    {imageGeneratedTopic && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          AI Analysis:
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={imageGeneratedTopic}
                          onChange={(e) =>
                            setImageGeneratedTopic(e.target.value)
                          }
                          sx={{ mb: 2 }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleGenerateFromImage}
                          disabled={loading}
                          sx={modernActionButtonStyle}
                        >
                          {loading
                            ? "Generating Post..."
                            : "Generate Post from Image"}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right Panel - Preview Section - Only show when content is generated */}
          {generatedContent && (
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "calc(100vh - 120px)",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "#2D3748",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  LinkedIn Preview
                </Typography>

                {generatedContent ? (
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <LinkedInPreview user={user}>
                      {isEditing ? (
                        <Box
                          sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                          }}
                        >
                          <TextField
                            multiline
                            rows={8}
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            variant="outlined"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.9rem",
                                lineHeight: 1.6,
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.6,
                            overflow: "visible",
                          }}
                        >
                          <TypeWriter text={generatedContent} speed={2} />
                        </Box>
                      )}
                    </LinkedInPreview>

                    {/* Content Scorer */}
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <ContentScorer
                        content={editedContent || generatedContent}
                        hasImage={false} // TODO: Add image support
                        hashtags={generatedHashtags}
                        isVisible={true}
                      />
                    </Box>

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: { xs: 0.5, sm: 2 },
                        mt: 3,
                        pt: 2,
                        borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                        flexWrap: "wrap",
                        px: 1,
                      }}
                    >
                      {isEditing ? (
                        <>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              setIsEditing(false);
                              setEditedContent("");
                            }}
                            sx={{
                              ...modernButtonStyle,
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setGeneratedContent(editedContent);
                              setIsEditing(false);
                            }}
                            sx={{
                              bgcolor: "#4890fe",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: { xs: "0.75rem", sm: "0.9rem" },
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "#3a7ad9",
                                boxShadow: "none",
                              },
                              px: { xs: 1, sm: 2 },
                              height: { xs: "36px", sm: "40px" },
                            }}
                          >
                            Save Changes
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={handleGenerateWrapper}
                            disabled={loading}
                            sx={{
                              ...modernButtonStyle,
                              borderColor: "#ff6b35",
                              color: "#ff6b35",
                              "&:hover": {
                                borderColor: "#e55a2b",
                                bgcolor: "rgba(255, 107, 53, 0.04)",
                              },
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={14} />
                            ) : (
                              "Regenerate"
                            )}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setIsEditing(true);
                              setEditedContent(generatedContent);
                            }}
                            sx={modernButtonStyle}
                          >
                            Edit
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<TuneIcon />}
                            onClick={(e) =>
                              setModificationAnchorEl(e.currentTarget)
                            }
                            sx={{
                              ...modernButtonStyle,
                              borderColor: "#9c27b0",
                              color: "#9c27b0",
                              "&:hover": {
                                borderColor: "#7b1fa2",
                                bgcolor: "rgba(156, 39, 176, 0.04)",
                              },
                            }}
                          >
                            AI Enhance
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddPhotoAlternateIcon />}
                            onClick={() => {}} // TODO: Add image attachment
                            sx={modernButtonStyle}
                          >
                            Image
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                            onClick={() => {}} // TODO: Add carousel attachment
                            sx={modernButtonStyle}
                          >
                            Carousel
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopy}
                            sx={{
                              ...modernButtonStyle,
                              borderColor: copySuccess ? "#22C55E" : "var(--primary-color)",
                              color: copySuccess ? "#22C55E" : "var(--primary-color)",
                              "&:hover": {
                                borderColor: copySuccess
                                  ? "#22C55E"
                                  : "var(--primary-dark)",
                                bgcolor: copySuccess
                                  ? "rgba(34, 197, 94, 0.04)"
                                  : "var(--primary-light)",
                              },
                            }}
                          >
                            {copySuccess ? "Copied!" : "Copy"}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saveLoading}
                            sx={modernButtonStyle}
                          >
                            {saveLoading ? (
                              <CircularProgress size={14} />
                            ) : (
                              "Save"
                            )}
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            startIcon={
                              isSharing ? (
                                <CircularProgress size={16} />
                              ) : (
                                <LinkedInIcon />
                              )
                            }
                            onClick={() => {}} // TODO: Add LinkedIn sharing
                            disabled={isSharing}
                            sx={{
                              bgcolor: "#0A66C2",
                              borderRadius: "8px",
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: { xs: "0.75rem", sm: "0.9rem" },
                              "&:hover": {
                                bgcolor: "#084482",
                              },
                              px: { xs: 1, sm: 2 },
                              height: { xs: "36px", sm: "40px" },
                            }}
                          >
                            Share
                          </Button>
                        </>
                      )}
                    </Box>

                    {/* Save/Error Messages */}
                    {saveSuccess && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        {saveSuccess}
                      </Alert>
                    )}

                    {saveError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {saveError}
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      textAlign: "center",
                      color: "#6c757d",
                    }}
                  >
                    <Typography>
                      Generate a post to see the LinkedIn preview
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* AI Enhancement Menu */}
        <Menu
          anchorEl={modificationAnchorEl}
          open={Boolean(modificationAnchorEl)}
          onClose={() => setModificationAnchorEl(null)}
          sx={{
            zIndex: 1300,
            "& .MuiPaper-root": {
              borderRadius: "4px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "200px",
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add make shorter functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            Make Shorter
          </MenuItem>
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add make longer functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            Make Longer
          </MenuItem>
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add more professional functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            More Professional
          </MenuItem>
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add more casual functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            More Casual
          </MenuItem>
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add add emojis functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            Add Emojis
          </MenuItem>
          <MenuItem
            onClick={() => {
              setModificationAnchorEl(null);
              // TODO: Add custom instructions functionality
            }}
            sx={{ fontSize: "0.9rem", py: 1.5 }}
          >
            Custom Instructions
          </MenuItem>
        </Menu>

        {/* Loading indicator for hooks */}
        {hookLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              mb: 3,
            }}
          >
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Generating hooks...</Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default GeneratePostNew;
